/**
 * In-app bug reports API (header widget).
 */

import { config } from '../../config'
import { createServiceFetch } from '../../utils/validateFetchUrl'

const bugReportsFetch = createServiceFetch(config.api.baseUrl)
import { ApiError, getTenantIdForRequest, request } from './core'

const { api: apiConfig, auth: authConfig } = config

export type BugReportMode = 'off' | 'manual' | 'auto'

export interface BugReportPublicConfig {
  enabled: boolean
  mode?: BugReportMode
  /** Ticketing system this tenant files into. */
  target_system?: 'azure_devops' | 'jira'
  project?: string | null
  work_item_type?: string | null
  capture_console?: boolean
  capture_api_errors?: boolean
  auto_report_frontend?: boolean
  /**
   * False when no drafting model is picked for the tenant — the mic and the
   * quick composer both stay hidden. Named `voice_*` on the wire because that
   * is the shipped contract; it gates all AI drafting, spoken or typed.
   */
  voice_drafting_enabled?: boolean
}

export interface BugReportSubmitResponse {
  success: boolean
  work_item_id?: number | null
  work_item_url?: string | null
  /** Readable Jira key (PROJ-123); null for Azure DevOps. */
  issue_key?: string | null
  attachments_uploaded: number
  message: string
}

/** Where the words came from; selects the drafting prompt on the server. */
export type BugReportInputMode = 'spoken' | 'typed'

/** Ticket fields drafted from a one-line report (see `bugReportsApi.draftTicket`). */
export interface BugReportDraft {
  title: string
  description: string
  severity: string
  work_item_type: string
  feature: string
  suggestions: string
  other_details: string
  /** 'ai' when the LLM drafted it; 'heuristic' when it fell back to raw words. */
  source: 'ai' | 'heuristic'
}

export const BUG_REPORT_STATUSES = [
  'Open',
  'In progress',
  'Hold',
  'Verification pending',
  'Done',
  'Re-Open',
] as const

export type BugReportStatus = (typeof BUG_REPORT_STATUSES)[number]

export type BugReportTab = 'all' | 'reported_by_me' | 'assigned_to_me'

export interface BugReportIssue {
  id: string
  tenant_id: string
  tenant_name?: string | null
  title: string
  description: string
  tags?: string | null
  severity?: string | null
  work_item_type?: string | null
  feature_group?: string | null
  workflow_id?: string | null
  feature?: string | null
  other_details?: string | null
  suggestions?: string | null
  /** Captured console/API logs + stack trace; only returned by getIssue (detail). */
  logs?: string | null
  page_url?: string | null
  reporter_user_id?: string | null
  reporter_email?: string | null
  reporter_name?: string | null
  assigned_to?: string | null
  /** True when assigned_to is masked (external assignee shown to a non-platform-admin). */
  assigned_to_masked?: boolean
  status: string
  source: string
  ado_work_item_id?: number | null
  ado_work_item_url?: string | null
  ado_sync_status: string
  /**
   * Readable Jira issue key (PROJ-123) for reports filed into Jira; null for Azure
   * DevOps. Its presence is what switches the ticket chip to Jira branding.
   */
  jira_issue_key?: string | null
  occurrence_count: number
  created_at?: number | null
  updated_at?: number | null
  /** When the work item was last edited on the ADO board (back-synced hourly). */
  ado_changed_date?: number | null
  /** Combined last-touched time = max(updated_at, ado_changed_date). */
  last_updated_at?: number | null
  /** Display-safe editor name (masked to "Support Team" for non-tenant editors). */
  last_updated_by?: string | null
  /** Editor email (null when masked). */
  last_updated_by_email?: string | null
}

export interface BugReportIssueList {
  items: BugReportIssue[]
  total: number
  page: number
  page_size: number
}

export interface BugReportIssueFilters {
  tab?: BugReportTab
  /** One or more tenant ids; empty/omitted means all tenants (platform admin). */
  tenant?: string[]
  /** One or more statuses; empty/omitted means all statuses. */
  status?: string[]
  assignee?: string
  reporter?: string
  dateFrom?: number
  dateTo?: number
  /** Case-insensitive title match; pure digits (optional leading #) also match ADO ticket id. */
  search?: string
  page?: number
  pageSize?: number
}

export interface BugReportIssueSummary {
  total: number
  by_status: Record<string, number>
}

export interface BugReportAssignableUser {
  email: string
  display_name?: string | null
  is_platform_admin?: boolean
}

export interface BugReportIssueUpdate {
  status?: BugReportStatus
  assigned_to?: string | null
  reported_by?: string | null
  severity?: string | null
  tags?: string | null
}

export interface BugReportBulkDeleteResult {
  /** Ids whose local row was removed (regardless of ADO outcome). */
  deleted: string[]
  /** Subset of `deleted` whose linked ADO work item could not be deleted. */
  ado_failed: string[]
  /** Ids the caller could not see or that no longer exist. */
  not_found: string[]
}

function getAssumedRoleIdForRequest(): string | null {
  try {
    const tenantId = localStorage.getItem('tagp-tenant-id')
    const key = tenantId ? `tagp-assumed-role-${tenantId}` : 'tagp-assumed-role'
    const stored = localStorage.getItem(key)
    if (stored) {
      const parsed = JSON.parse(stored) as { id?: string }
      return parsed?.id || null
    }
  } catch {
    /* ignore */
  }
  return null
}

function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {}
  try {
    const token = localStorage.getItem(authConfig.tokenKey)
    if (token) {
      headers.Authorization = `Bearer ${token}`
      const tenantId = localStorage.getItem('tagp-tenant-id') || getTenantIdForRequest()
      if (tenantId) headers['X-Tenant-ID'] = tenantId
    } else {
      const tenantId = getTenantIdForRequest()
      if (tenantId) headers['X-Tenant-ID'] = tenantId
    }
  } catch {
    /* ignore */
  }
  const assumedRoleId = getAssumedRoleIdForRequest()
  if (assumedRoleId) headers['X-Assume-Role-Id'] = assumedRoleId
  return headers
}

/**
 * Backend Bugsnap errors often wrap a raw Azure DevOps payload, e.g.
 * `Azure DevOps returned HTTP 404 ...: {"$id":"1",...,"message":"TF200016: The following
 * project does not exist: TAGP-Proje. Verify ..."}`. Surface the inner human-readable
 * message so the user learns what to fix (wrong Organization URL / Project) instead of a
 * generic failure or a JSON blob.
 */
function humanizeSubmitError(detail: string): string {
  const match = detail.match(/\{.*\}/s)
  if (match) {
    try {
      const inner = JSON.parse(match[0])
      if (typeof inner?.message === 'string' && inner.message.trim()) {
        // Drop Azure's leading error code (e.g. "TF200016: ", "VS403386: ").
        return inner.message.trim().replace(/^[A-Z]{2}\d+:\s*/, '')
      }
    } catch {
      /* not JSON, fall through */
    }
  }
  return detail
}

export const bugReportsApi = {
  getConfig: () => request<BugReportPublicConfig>('/v1/bug-reports/config'),

  /**
   * Draft ticket fields from a one-line report, spoken or typed. Drafting only —
   * this does not create a work item; the caller still submits through `submit()`.
   */
  draftTicket: (params: {
    text: string
    inputMode: BugReportInputMode
    pageUrl?: string
  }): Promise<BugReportDraft> =>
    request<BugReportDraft>('/v1/bug-reports/draft', {
      method: 'POST',
      body: JSON.stringify({
        // `transcript` is the field name the endpoint has always taken; the
        // server accepts `report_text` as an alias but there is no reason to
        // send the newer name.
        transcript: params.text,
        input_mode: params.inputMode,
        page_url: params.pageUrl ?? null,
      }),
    }),

  submit: async (params: {
    title: string
    description: string
    pageUrl: string
    severity?: string
    userAgent?: string
    workItemType?: string
    customer?: string
    featureGroup?: string
    workflowId?: string
    feature?: string
    otherDetails?: string
    suggestions?: string
    files: File[]
  }): Promise<BugReportSubmitResponse> => {
    const formData = new FormData()
    formData.append('title', params.title)
    formData.append('description', params.description)
    formData.append('page_url', params.pageUrl)
    if (params.severity) formData.append('severity', params.severity)
    if (params.userAgent) formData.append('user_agent', params.userAgent)
    if (params.workItemType) formData.append('work_item_type', params.workItemType)
    if (params.customer) formData.append('customer', params.customer)
    if (params.featureGroup) formData.append('feature_group', params.featureGroup)
    if (params.workflowId) formData.append('workflow_id', params.workflowId)
    if (params.feature) formData.append('feature', params.feature)
    if (params.otherDetails) formData.append('other_details', params.otherDetails)
    if (params.suggestions) formData.append('suggestions', params.suggestions)
    for (const file of params.files) {
      formData.append('files', file, file.name)
    }

    const url = `${apiConfig.baseUrl}/v1/bug-reports`
    const response = await bugReportsFetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
    })

    if (!response.ok) {
      let detail: unknown = 'Failed to submit Bugsnap'
      try {
        const body = await response.json()
        detail = body.error?.message || body.detail || body.message || body.error_code || detail
      } catch {
        detail = (await response.text().catch(() => '')) || detail
      }
      const message = typeof detail === 'string' ? detail : JSON.stringify(detail)
      throw new ApiError(humanizeSubmitError(message), response.status)
    }
    return response.json() as Promise<BugReportSubmitResponse>
  },

  listIssues: (filters: BugReportIssueFilters = {}): Promise<BugReportIssueList> => {
    const params = new URLSearchParams()
    if (filters.tab) params.set('tab', filters.tab)
    for (const t of filters.tenant ?? []) params.append('tenant', t)
    for (const s of filters.status ?? []) params.append('status', s)
    if (filters.assignee) params.set('assignee', filters.assignee)
    if (filters.reporter) params.set('reporter', filters.reporter)
    if (filters.dateFrom != null) params.set('date_from', String(filters.dateFrom))
    if (filters.dateTo != null) params.set('date_to', String(filters.dateTo))
    if (filters.search) params.set('search', filters.search)
    if (filters.page != null) params.set('page', String(filters.page))
    if (filters.pageSize != null) params.set('page_size', String(filters.pageSize))
    const qs = params.toString()
    const querySuffix = qs ? `?${qs}` : ''
    return request<BugReportIssueList>(`/v1/bug-reports/issues${querySuffix}`)
  },

  getIssue: (id: string): Promise<BugReportIssue> =>
    request<BugReportIssue>(`/v1/bug-reports/issues/${encodeURIComponent(id)}`),

  assignableUsers: (tenant: string): Promise<BugReportAssignableUser[]> =>
    request<BugReportAssignableUser[]>(
      `/v1/bug-reports/assignable-users?tenant=${encodeURIComponent(tenant)}`,
    ),

  getSummary: (filters: Pick<BugReportIssueFilters, 'tab' | 'tenant'> = {}): Promise<BugReportIssueSummary> => {
    const params = new URLSearchParams()
    if (filters.tab) params.set('tab', filters.tab)
    for (const t of filters.tenant ?? []) params.append('tenant', t)
    const qs = params.toString()
    const querySuffix = qs ? `?${qs}` : ''
    return request<BugReportIssueSummary>(`/v1/bug-reports/issues/summary${querySuffix}`)
  },

  updateIssue: (id: string, patch: BugReportIssueUpdate): Promise<BugReportIssue> =>
    request<BugReportIssue>(`/v1/bug-reports/issues/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  deleteIssue: (id: string): Promise<void> =>
    request<void>(`/v1/bug-reports/issues/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  bulkDeleteIssues: (ids: string[]): Promise<BugReportBulkDeleteResult> =>
    request<BugReportBulkDeleteResult>('/v1/bug-reports/issues/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }),

  exportIssues: async (
    format: 'csv' | 'excel',
    filters: BugReportIssueFilters = {},
  ): Promise<Blob> => {
    const params = new URLSearchParams()
    params.set('format', format)
    if (filters.tab) params.set('tab', filters.tab)
    for (const t of filters.tenant ?? []) params.append('tenant', t)
    for (const s of filters.status ?? []) params.append('status', s)
    if (filters.assignee) params.set('assignee', filters.assignee)
    if (filters.reporter) params.set('reporter', filters.reporter)
    if (filters.dateFrom != null) params.set('date_from', String(filters.dateFrom))
    if (filters.dateTo != null) params.set('date_to', String(filters.dateTo))
    if (filters.search) params.set('search', filters.search)
    const url = `${apiConfig.baseUrl}/v1/bug-reports/issues/export?${params.toString()}`
    const response = await bugReportsFetch(url, { method: 'GET', headers: getAuthHeaders() })
    if (!response.ok) {
      throw new ApiError('Failed to export bug reports', response.status)
    }
    return response.blob()
  },
}
